/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';

import {observer} from 'mobx-react';
import {observable, action} from 'mobx';

/**************************************** PROJECT MODULES *****************************************/
import {CookieConsentBarView} from './cookie-consent-bar-view';
import {ConfigProps} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
// Grabs props from 'this' value of below component, omitting values in 2nd arg
type CookieConsentBarContainerPropsNoReact = OmitReactProperties<CookieConsentBarContainer>;

// Props shared by both container & view
interface CookieConsentBarPropsShared {}

// Container props for current file's component
interface CookieConsentBarContainerProps extends CookieConsentBarPropsShared {}

/**
 * View props for UI component rendered by this file's container component
 */
export interface CookieConsentBarViewProps
    extends CookieConsentBarPropsShared,
        CookieConsentBarContainerPropsNoReact,
        ConfigProps {}

/********************************************* CONFIG *********************************************/
const fadeTopbarLengthMs = 1000;

/********************************************* EXPORT *********************************************/
/**
 * Cookie consent bar
 *
 * Should appear if user has not yet consented
 * Remains hidden once user consents (confirming saves value in local storage)
 */
@observer
export class CookieConsentBarContainer extends React.Component<CookieConsentBarContainerProps> {
    /**
     * If true, don't display the bar - the user has consented
     */
    @observable
    public hasConsented = true; // TODO: Reset back to false

    /**
     * If true, the animation for fading out the consent bar has ended
     */
    @observable
    public animationEnd = false;

    /**
     * Constructor
     *
     * Determines whether to display consent bar by checking localStorage for
     * whether user has previously consented to use of cookies
     */
    constructor(props: CookieConsentBarContainerProps) {
        super(props);
        // Check if user has consented
        if (localStorage.getItem(`cookieconsent_status`)) {
            this.animationEnd = true;
            this.setConsented();
        }
    }

    /**
     * Set that user has consented
     * Triggers animation to fade out consent bar; hides it when animation done
     */
    @action
    public setConsented = () => {
        localStorage.setItem('cookieconsent_status', 'dismiss');
        this.hasConsented = true;
        setTimeout(action(() => (this.animationEnd = true)), fadeTopbarLengthMs);
    };

    render() {
        return (
            <CookieConsentBarView
                hasConsented={this.hasConsented}
                setConsented={this.setConsented}
                animationEnd={this.animationEnd}
            />
        );
    }
}
